import { CDNCharactersAddress, webchatTokenRetrievalAddress, defaultDirectLineAddress } from '../models/constants'
import { Mode } from '../models/enums'
import { RetorikLanguageData, RetorikMainComponentConfiguration, RetorikMainComponentProps, RetorikOtherData } from '../models/retorikTypes'

const getRetorikConfigs = async (props: RetorikMainComponentProps, baseAddress: string): Promise<(RetorikMainComponentConfiguration & RetorikLanguageData & RetorikOtherData) | null> => {
  const retorikProps = { ...props }
  let res: (RetorikMainComponentConfiguration & RetorikLanguageData & RetorikOtherData) | null = null

  const fullAddress = `${baseAddress}api/integration/framework`

  try {
    const response = await fetch(fullAddress)
    const json = await response.json()
    res = processJson(json, retorikProps, baseAddress)
  } catch (error) {
    console.log(error)
  }

  return res
}

const getAddressWithoutTenant = (address: string, tenant: string | undefined): string => {
  if (tenant && address.indexOf(`/${tenant}`)) {
    address = address.replace(`/${tenant}`, '')
  }

  if (address.endsWith('/')) {
    return address.slice(0, -1)
  }

  return address
}

const processJson = (json: any, retorikProps: RetorikMainComponentProps, baseAddress: string): (RetorikMainComponentConfiguration & RetorikLanguageData & RetorikOtherData) | null => {
  if (json) {
    // Get full address for backgrounds & logo
    let logo, background, newsBackground, personnage
    if (json.personnage) {
      if (json.personnage.indexOf('http') === -1) {
        personnage = `${CDNCharactersAddress}/${json.personnage}`
      } else {
        personnage = json.personnage
      }
    } else {
      personnage = `${CDNCharactersAddress}/long-shot/zahra`
    }

    const addressWithoutTenant = getAddressWithoutTenant(baseAddress, retorikProps.addressData.tenant)

    if (json.logo) {
      logo = `${addressWithoutTenant}${json.logo}`
    }
    if (json.background?.length) {
      background = `${addressWithoutTenant}${json.background}`
    } else {
      background = new URL('../assets/images/background.webp', import.meta.url)
    }
    if (json.newsBackground?.length) {
      newsBackground = `${addressWithoutTenant}${json.newsBackground}`
    } else {
      newsBackground = new URL('../assets/images/background.webp', import.meta.url)
    }

    // Handle size & height : string or number
    let size = json.agentSize || undefined
    if (json.agentSize && !isNaN(+json.agentSize)) {
      size = +json.agentSize // convert to int
    }
    let height = json.agentHeight || undefined
    if (json.agentHeight && !isNaN(+json.agentHeight)) {
      height = +json.agentHeight // convert to int
    }

    // For widget
    // Handle size & height : string or number
    let sizeWidget = json.agentSizeWidget || json.agentSize || undefined
    if (json.agentSizeWidget && !isNaN(+json.agentSizeWidget)) {
      sizeWidget = +json.agentSizeWidget // convert to int
    }
    let heightWidget = json.agentHeightWidget || json.agentHeight || undefined
    if (json.agentHeightWidget && !isNaN(+json.agentHeightWidget)) {
      heightWidget = +json.agentHeightWidget // convert to int
    }

    // Process token endpoint from manual configuration and incoming data
    let tokenEndpoint = ''
    if (retorikProps.addressData?.directline?.tokenEndpoint) {
      tokenEndpoint = retorikProps.addressData.directline.tokenEndpoint
    } else if (retorikProps.addressData?.baseURI?.includes(webchatTokenRetrievalAddress)) {
      tokenEndpoint = retorikProps.addressData.baseURI
    } else if (json.directLine_tokenEndpoint) {
      tokenEndpoint = json.directLine_tokenEndpoint
    } else {
      tokenEndpoint = `${baseAddress}${webchatTokenRetrievalAddress}`
    }

    // Prevent passive speech recognition if no name was given for the character.
    // Used for retrocompatibility since v2.0.4
    if (!json.characterName && json.speechRecognitionOptions) {
      ;(json.speechRecognitionOptions.enableContinuousRecognitionOnAllDevices = false), (json.speechRecognitionOptions.enableContinuousRecognitionOn = undefined)
    }

    const props: RetorikMainComponentConfiguration & RetorikLanguageData & RetorikOtherData = {
      config: {
        fullSize: undefined,
        isUsedOnBorne: undefined,
        locales: {
          getDefaultFromBrowser: undefined
        },
        position: {
          searchForLocation: json.detectLocation,
          latitude: undefined,
          longitude: undefined,
          city: undefined,
          country: undefined
        },
        subtitles: json.subtitles,
        subtitlesPosition: json.subtitlesPosition,
        logo: json.logo ? { src: logo } : undefined,
        answerpanel: undefined,
        skipWelcome: json.skipWelcome,
        preventExpectedInputHint: json.preventExpectedInputHint,
        pagination: undefined,
        remote: undefined,
        languageChoiceBottom: undefined,
        companyName: json.companyName,
        companyNameLocalized: json.companyNameLocalized,
        subCompanyMessage: json.subCompanyMessage,
        subCompanyMessageLocalized: json.subCompanyMessageLocalized,
        loaderInformationTexts: {
          text: {
            top: json.textModeHomeTitle,
            bottom: json.textModeHomeSubtitle
          },
          vocal: {
            top: json.voiceModeHomeTitle,
            bottom: json.voiceModeHomeSubtitle
          }
        },
        loaderInformationTextsLocalized: {
          text: {
            top: json.textModeHomeTitleLocalized,
            bottom: json.textModeHomeSubtitleLocalized
          },
          vocal: {
            top: json.voiceModeHomeTitleLocalized,
            bottom: json.voiceModeHomeSubtitleLocalized
          }
        },
        hideRetorikLogo: json.hideRetorikLogo,
        baseMenu: undefined,
        customMenu: undefined,
        subMenu: undefined,
        menu: json.menu,
        hideMenu: json.hideMenu,
        hideControls: json.hideControls,
        doNotDetectDeviceFromUserAgent: json.doNotDetectDeviceFromUserAgent,
        enableConversationCookie: json.enableConversationCookie,
        conversationCookieMaxAge: json.conversationCookieMaxAge,
        enableTelemetry: json.enableTelemetry,
        allowSwitchLayout: json.allowSwitchLayout,
        forceMobileView: json.forceMobileFullScreenLayout,
        enableSpeechCaching: json.enableSpeechCaching,
        speechRecognitionOptions: {
          timerBeforeSpeechEnd: json.speechRecognitionOptions?.timerBeforeSpeechEnd,
          enableActivationSound: json.speechRecognitionOptions?.enableActivationSound,
          enableContinuousRecognitionOnAllDevices: json.speechRecognitionOptions?.enableContinuousRecognitionOnAllDevices,
          enableContinuousRecognitionOn: json.speechRecognitionOptions?.enableContinuousRecognitionOn,
          wakeWords: json.speechRecognitionOptions?.wakeWords,
          grammars: json.speechRecognitionOptions?.grammars,
          inputHintBehaviour: json.speechRecognitionOptions?.inputHintBehaviour
        },
        speechSynthesisOptions: json.speechSynthesisOptions,
        enableDocumentPrinting: undefined,
        disableSpeechMode: json.disableSpeechMode,
        disableSound: json.disableSound,
        timerForFilterSelectionListMode: undefined,
        // Pattern != null means not null nor undefined
        useOldRemote: json.useOldRemote != null ? json.useOldRemote : true,
        preventVirtualKeyboard: undefined,
        channelData: json.channelData
      },
      viewsConfig: {
        homeRoute: 'home',
        webcamRotation: undefined,
        views: {
          home: {
            background: {
              style: 'image',
              image: background,
              video: undefined,
              webcam: undefined
            }
          },
          news: {
            intervalInSeconds: json.newsConfig?.intervalInSeconds || 2,
            loop: true,
            openingVideo: json.newsConfig?.openingVideo,
            endingVideo: json.newsConfig?.endingVideo,
            background: {
              style: 'image',
              image: newsBackground,
              video: undefined,
              webcam: undefined
            },
            showMenu: undefined,
            bannerTitle: json.newsConfig?.bannerTitle,
            bannerTitleLocalized: json.newsConfig?.bannerTitleLocalized,
            removeTitleFromTextToSpeak: json.newsConfig?.removeTitleFromTextToSpeak,
            removeSubtitleFromTextToSpeak: json.newsConfig?.removeSubtitleFromTextToSpeak,
            bannerPosition: json.newsConfig?.bannerPosition,
            mediaPosition: json.newsConfig?.mediaPosition
          }
        }
      },
      chatbotData: {
        size: size,
        height: height
      },
      chatbotDataWidget: {
        size: sizeWidget,
        height: heightWidget
      },
      agentData: {
        url: personnage,
        name: json.characterName,
        animationsUrl: json.animationsUrl || undefined
      },
      ponyfillFactoryCredentials: {
        region: json.voiceCredentialsRegion,
        subscriptionKey: json.voiceCredentialsSubscriptionKey,
        speechRecognitionHostnamesRecord: json.speechRecognizerHosts,
        speechRecognitionHostname: undefined,
        speechSynthesisHostname: json.speechSynthesizerHost
      },
      addressData: {
        ...retorikProps.addressData,
        directline: {
          tokenEndpoint: tokenEndpoint,
          address: retorikProps.addressData?.directline?.address || json.directLine_address || defaultDirectLineAddress
        }
      },
      userData: retorikProps.userData,
      customVoice: {
        voice: json.voice,
        gender: undefined
      },
      colors: {
        theme: json.colorSettings.theme,
        primary: json.colorSettings.primary,
        secondary: json.colorSettings.secondary,
        black: json.colorSettings.black,
        whereToEatColor: json.colorSettings.whereToEatColor,
        whereToSleepColor: json.colorSettings.whereToSleepColor,
        tobeSeenColor: json.colorSettings.tobeSeenColor,
        tobeDoneColor: json.colorSettings.tobeDoneColor,
        localProductsColor: json.colorSettings.localProductsColor,
        servicesColor: json.colorSettings.servicesColor,
        companyName: json.colorSettings.companyName,
        agendaColor: json.colorSettings.agendaColor,
        occupantColor: json.colorSettings.occupantColor,
        loader: {
          animation: json.colorSettings.loader?.animation,
          background: json.colorSettings.loader?.background,
          text: json.colorSettings.loader?.text,
          toggle: {
            text: json.colorSettings.loader?.toggle?.text,
            background: json.colorSettings.loader?.toggle?.background
          },
          button: {
            background: {
              default: json.colorSettings.loader?.button?.background?.default,
              hover: json.colorSettings.loader?.button?.background?.hover
            },
            border: {
              default: json.colorSettings.loader?.button?.border?.default,
              hover: json.colorSettings.loader?.button?.border?.hover
            },
            text: {
              default: json.colorSettings.loader?.button?.text?.default,
              hover: json.colorSettings.loader?.button?.text?.hover
            }
          }
        },
        card: {
          frame: {
            background: json.colorSettings.card?.frame?.background,
            border: json.colorSettings.card?.frame?.border,
            text: json.colorSettings.card?.frame?.text
          },
          button: {
            background: {
              default: json.colorSettings.card?.button?.background?.default,
              hover: json.colorSettings.card?.button?.background?.hover
            },
            border: {
              default: json.colorSettings.card?.button?.border?.default,
              hover: json.colorSettings.card?.button?.border?.hover
            },
            text: {
              default: json.colorSettings.card?.button?.text?.default,
              hover: json.colorSettings.card?.button?.text?.hover
            }
          },
          discoverButton: {
            background: {
              default: json.colorSettings.card?.discoverButton?.background?.default,
              hover: json.colorSettings.card?.discoverButton?.background?.hover
            },
            border: {
              default: json.colorSettings.card?.discoverButton?.border?.default,
              hover: json.colorSettings.card?.discoverButton?.border?.hover
            },
            text: {
              default: json.colorSettings.card?.discoverButton?.text?.default,
              hover: json.colorSettings.card?.discoverButton?.text?.hover
            }
          }
        },
        textMode: {
          panel: {
            background: json.colorSettings.textMode?.panel?.background,
            border: json.colorSettings.textMode?.panel?.border,
            conversationUser: json.colorSettings.textMode?.panel?.conversationUser,
            conversationBot: json.colorSettings.textMode?.panel?.conversationBot,
            backgroundUser: json.colorSettings.textMode?.panel?.backgroundUser,
            backgroundBot: json.colorSettings.textMode?.panel?.backgroundBot
          }
        },
        vocalMode: {
          subtitles: {
            text: json.colorSettings.vocalMode?.subtitles?.text,
            background: json.colorSettings.vocalMode?.subtitles?.background
          }
        },
        formInput: {
          text: {
            default: json.colorSettings.formInput?.inputText?.default,
            hover: json.colorSettings.formInput?.inputText?.hover
          },
          inputRadioCheckbox: {
            unchecked: {
              background: json.colorSettings.formInput?.inputRadioCheckbox?.unchecked?.background,
              border: json.colorSettings.formInput?.inputRadioCheckbox?.unchecked?.border
            },
            checked: {
              background: json.colorSettings.formInput?.inputRadioCheckbox?.checked?.background,
              border: json.colorSettings.formInput?.inputRadioCheckbox?.checked?.border,
              item: json.colorSettings.formInput?.inputRadioCheckbox?.checked?.item
            }
          },
          inputButton: {
            background: {
              default: json.colorSettings.formInput?.inputButton?.background?.default,
              hover: json.colorSettings.formInput?.inputButton?.background?.hover
            },
            border: {
              default: json.colorSettings.formInput?.inputButton?.border?.default,
              hover: json.colorSettings.formInput?.inputButton?.border?.hover
            },
            text: {
              default: json.colorSettings.formInput?.inputButton?.text?.default,
              hover: json.colorSettings.formInput?.inputButton?.text?.hover
            }
          },
          likert: {
            default: json.colorSettings.formInput?.likert?.default,
            hover: json.colorSettings.formInput?.likert?.hover
          }
        }
      },
      defaultMode: json.disableSpeechMode ? Mode.text : json.defaultMode,
      skipLoader: json.skipLoader,
      // Language data
      defaultCulture: json.defaultCulture,
      supportedCultures: json.supportedCultures,
      // Other data
      disableHistory: json.disableHistory,
      customScripts: json.customScripts,
      customCSS: json.customCSS,
      features: json.features
    }

    return props
  }

  return null
}

export default getRetorikConfigs
